import React from 'react';
import { Monitor, Smartphone, Tablet } from 'lucide-react';
import { Card, Skel, Tween } from './parts';
import { int, Visitors } from './api';

const DEVICE_ICON: Record<string, React.ComponentType<{ className?: string }>> = { Desktop: Monitor, Mobile: Smartphone, Tablet };

/** Devices as three meters, then the browsers visits used. */
export const DevicesCard: React.FC<{ v: Visitors | null; loading: boolean }> = ({ v, loading }) => {
  if (!v) {
    return (
      <Card i={4} area="dev" title="Devices">
        {loading ? <Skel className="min-h-[220px] flex-1" /> : <p className="oa-cap">Devices appear once PostHog is connected.</p>}
      </Card>
    );
  }

  const total = Math.max(1, v.totals.sessions);
  // Always show the three device kinds, so a missing one reads as 0% rather than vanishing.
  const devices = ['Desktop', 'Mobile', 'Tablet'].map((name) => ({ name, sessions: v.devices.find((d) => d.device === name)?.sessions ?? 0 }));
  const browsers = v.browsers ?? [];
  const browserMax = Math.max(1, ...browsers.map((b) => b.sessions));

  return (
    <Card i={4} area="dev" title="Devices">
      <ul className="oa-devices">
        {devices.map((d, i) => {
          const Icon = DEVICE_ICON[d.name];
          const pct = (d.sessions / total) * 100;
          return (
            <li key={d.name} style={{ ['--i' as string]: i, ['--h' as string]: `${Math.max(pct, 2)}%` } as React.CSSProperties} title={`${d.name}: ${int(d.sessions)} of ${int(v.totals.sessions)} sessions`}>
              <span className="oa-meter" aria-hidden="true">
                <i />
              </span>
              <span className="flex items-center gap-1.5 text-[12px] text-[var(--oa-muted)]">
                <Icon className="h-3.5 w-3.5" />
                {d.name}
              </span>
              <b className="oa-big text-[26px]">
                <Tween value={pct} format={(n) => `${Math.round(n)}%`} />
              </b>
              <em>{int(d.sessions)} sessions</em>
            </li>
          );
        })}
      </ul>
      <div className="flex flex-1 flex-col gap-2.5">
        <h3 className="oa-sub">Browsers</h3>
        {browsers.length === 0 ? (
          <p className="oa-cap">No browsers recorded in this period.</p>
        ) : (
          <ol className="oa-bars flex-1">
            {browsers.map((b, i) => (
              <li key={b.browser} style={{ ['--i' as string]: i } as React.CSSProperties}>
                <span className="truncate">{b.browser}</span>
                <b>{Math.round((b.sessions / total) * 100)}%</b>
                <i style={{ ['--w' as string]: `${(b.sessions / browserMax) * 100}%` } as React.CSSProperties} />
                <em>{int(b.sessions)} sessions</em>
              </li>
            ))}
          </ol>
        )}
      </div>
    </Card>
  );
};
